/**
 * Login-time Terms & Conditions acceptance gate (V2).
 *
 * Mirrors the V3 TermsController: resolves the gate decision ONCE per login from
 * `GET /v2/terms/acceptance/status` (the API reads the Datadog flag server-side) and caches it
 * for the session — navigation does not re-query. The status check FAILS OPEN: any error is
 * treated as "not required" so a check that can't complete never locks a user out. The acceptance
 * write (`POST /v2/terms/acceptance`) fails closed — the caller keeps the user on `/terms` until 201.
 *
 * The recorded version is resolved server-side from the flag, so no request body is sent.
 */
export default class TermsService {
  /** @ngInject **/
  constructor($http, $q, ngAuthSettings) {
    this.$http = $http;
    this.$q = $q;
    this.serviceBase = ngAuthSettings.apiServiceBaseUri;

    // Session-cached gate decision: { required }.
    this._status = null;
    this._resolved = false;
    this._resolvePromise = null;
  }

  /** `true` once the gate decision is settled (successful check OR fail-open). */
  get isResolved() {
    return this._resolved;
  }

  /** `true` only when the flag is on AND the user has not accepted the current version. */
  get mustAccept() {
    return !!(this._status && this._status.required === true);
  }

  /**
   * Resolve the gate decision once per login. Subsequent calls return the cached result.
   * Always resolves (never rejects) — errors fail open to "not required".
   */
  resolve() {
    if (this._resolved) {
      return this.$q.when(this._status);
    }
    if (this._resolvePromise) {
      return this._resolvePromise;
    }

    this._resolvePromise = this.$http
      .get(this.serviceBase + 'terms/acceptance/status')
      .then((response) => {
        const data = response.data || {};
        this._status = {
          required: data.required === true
        };
        this._resolved = true;
        return this._status;
      })
      .catch(() => {
        // FAIL OPEN — allow navigation; the user is re-prompted on next login.
        this._status = { required: false };
        this._resolved = true;
        return this._status;
      });

    return this._resolvePromise;
  }

  /** Records acceptance. Version is resolved server-side from the flag, so no body is sent. */
  accept() {
    return this.$http.post(this.serviceBase + 'terms/acceptance', null);
  }

  /** Opens the in-session gate after a successful accept, without a re-query. */
  markAccepted() {
    if (this._status) {
      this._status = angular.extend({}, this._status, { required: false });
    }
  }

  /** Clears the cached decision so the next login re-checks (called on logout). */
  reset() {
    this._status = null;
    this._resolved = false;
    this._resolvePromise = null;
  }
}
